import {
  resolvePrimaryPrivyWallet,
  resolvePrivyWallets,
  type WalletUserLike,
  type ResolvedWallet,
} from "~/lib/auth/wallet-resolution";
import type { VmId, VmPreference } from "~/lib/auth/vm-types";

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const SUI_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{64}$/;

function normalizeAddress(address: string): string {
  const trimmed = address.trim();
  return EVM_ADDRESS_PATTERN.test(trimmed) || SUI_ADDRESS_PATTERN.test(trimmed)
    ? trimmed.toLowerCase()
    : trimmed;
}

export interface PrincipalWallet extends ResolvedWallet {
  normalizedAddress: string;
}

export interface PrincipalWalletSet {
  ownerAddressSet: ReadonlySet<string>;
  ownerAddresses: string[];
  primaryWallet: PrincipalWallet | null;
  wallets: PrincipalWallet[];
}

export function createPrincipalWalletSet(input: {
  user: WalletUserLike;
  vmPreference?: VmPreference;
}): PrincipalWalletSet {
  const walletsByAddress = new Map<string, PrincipalWallet>();

  for (const wallet of resolvePrivyWallets(input.user)) {
    const normalizedAddress = normalizeAddress(wallet.address);
    if (walletsByAddress.has(normalizedAddress)) {
      continue;
    }

    walletsByAddress.set(normalizedAddress, {
      ...wallet,
      normalizedAddress,
    });
  }

  const wallets = Array.from(walletsByAddress.values());
  const primaryCandidate = resolvePrimaryPrivyWallet(
    input.user,
    input.vmPreference ?? "auto",
  );
  const primaryWallet = primaryCandidate
    ? (wallets.find(
        (wallet) =>
          wallet.normalizedAddress ===
          normalizeAddress(primaryCandidate.address),
      ) ?? null)
    : null;

  const ownerAddresses = wallets.map((wallet) => wallet.normalizedAddress);
  return {
    ownerAddressSet: new Set(ownerAddresses),
    ownerAddresses,
    primaryWallet,
    wallets,
  };
}

function findWalletByOwnerAddress(
  principalWallets: PrincipalWalletSet,
  ownerAddress: string,
): PrincipalWallet | null {
  const normalizedOwnerAddress = normalizeAddress(ownerAddress);
  return (
    principalWallets.wallets.find(
      (wallet) => wallet.normalizedAddress === normalizedOwnerAddress,
    ) ?? null
  );
}

function findWalletByVm(
  principalWallets: PrincipalWalletSet,
  ownerVm: VmId,
): PrincipalWallet | null {
  return (
    principalWallets.wallets.find((wallet) => wallet.vmId === ownerVm) ?? null
  );
}

export interface PortfolioMutationRoute {
  vmPreference?: VmId;
  walletAddress?: string;
}

export function resolvePortfolioMutationRoute(input: {
  ownerAddress: string;
  ownerVm: VmId;
  principalWallets: PrincipalWalletSet;
}): PortfolioMutationRoute | null {
  const ownerWallet = findWalletByOwnerAddress(
    input.principalWallets,
    input.ownerAddress,
  );
  if (ownerWallet) {
    return {
      vmPreference: ownerWallet.vmId,
      walletAddress: ownerWallet.address,
    };
  }

  const vmWallet = findWalletByVm(input.principalWallets, input.ownerVm);
  if (vmWallet) {
    return {
      vmPreference: vmWallet.vmId,
      walletAddress: vmWallet.address,
    };
  }

  if (
    input.principalWallets.primaryWallet &&
    input.principalWallets.primaryWallet.vmId === input.ownerVm
  ) {
    return {
      vmPreference: input.principalWallets.primaryWallet.vmId,
      walletAddress: input.principalWallets.primaryWallet.address,
    };
  }

  return null;
}
